import React, { useState, useEffect } from 'react';
import { Search, X, User, Mail, Phone, Globe, Server, Users, Calendar, DollarSign, Plus, Tag, Eye, EyeOff, Wifi, MapPin, Lock } from 'lucide-react';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const UserSearchModal = ({ isOpen, onClose }) => {
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Tab activo (routers, planes, pos, vendedores)
  const [activeTab, setActiveTab] = useState('routers');
  
  // Estados para mostrar contraseñas
  const [visiblePasswords, setVisiblePasswords] = useState({});
  
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountData, setDiscountData] = useState({
    amount: '',
    type: 'once',
    duration_months: 1
  });

  // Reset modal cuando se cierra
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedUser(null);
      setActiveTab('routers');
      setVisiblePasswords({});
    }
  }, [isOpen]);

  // Colores para agrupar por router
  const routerColors = [
    { bg: isDark ? 'bg-blue-900/30' : 'bg-blue-50', border: isDark ? 'border-blue-700' : 'border-blue-200', text: isDark ? 'text-blue-400' : 'text-blue-700' },
    { bg: isDark ? 'bg-green-900/30' : 'bg-green-50', border: isDark ? 'border-green-700' : 'border-green-200', text: isDark ? 'text-green-400' : 'text-green-700' },
    { bg: isDark ? 'bg-purple-900/30' : 'bg-purple-50', border: isDark ? 'border-purple-700' : 'border-purple-200', text: isDark ? 'text-purple-400' : 'text-purple-700' },
    { bg: isDark ? 'bg-orange-900/30' : 'bg-orange-50', border: isDark ? 'border-orange-700' : 'border-orange-200', text: isDark ? 'text-orange-400' : 'text-orange-700' },
    { bg: isDark ? 'bg-pink-900/30' : 'bg-pink-50', border: isDark ? 'border-pink-700' : 'border-pink-200', text: isDark ? 'text-pink-400' : 'text-pink-700' },
    { bg: isDark ? 'bg-cyan-900/30' : 'bg-cyan-50', border: isDark ? 'border-cyan-700' : 'border-cyan-200', text: isDark ? 'text-cyan-400' : 'text-cyan-700' },
  ];

  const getRouterColor = (index) => routerColors[index % routerColors.length];

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const response = await api.get(`/api/admin/users/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data.users || []);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error buscando usuarios:', error);
      alert('Error al buscar usuarios: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadUserDetails = async (userId) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/admin/users/${userId}/details`);
      setSelectedUser(response.data);
      setSearchResults([]);
      setActiveTab('routers');
    } catch (error) {
      console.error('Error cargando detalles:', error);
      alert('Error al cargar detalles: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (key) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleAddRouter = async () => {
    if (!confirm('¿Agregar un router adicional a este usuario? Se reflejará en su próximo pago.')) return;
    try {
      await api.post(`/api/admin/users/${selectedUser.user.id}/add-router`);
      alert('✅ Router adicional agregado');
      loadUserDetails(selectedUser.user.id);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddSeller = async () => {
    if (!confirm('¿Agregar un vendedor adicional a este usuario? Se reflejará en su próximo pago.')) return;
    try {
      await api.post(`/api/admin/users/${selectedUser.user.id}/add-seller-slot`);
      alert('✅ Slot de vendedor adicional agregado');
      loadUserDetails(selectedUser.user.id);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountData.amount || parseFloat(discountData.amount) <= 0) {
      alert('Ingresa un monto válido');
      return;
    }
    try {
      await api.post(`/api/admin/users/${selectedUser.user.id}/apply-discount`, discountData);
      alert('✅ Descuento aplicado correctamente');
      setShowDiscountModal(false);
      setDiscountData({ amount: '', type: 'once', duration_months: 1 });
      loadUserDetails(selectedUser.user.id);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const calculateNextPaymentDate = (registrationDate) => {
    const regDate = new Date(registrationDate);
    const regDay = regDate.getDate();
    const paymentDay = regDay >= 29 ? 28 : regDay;
    const today = new Date();
    const nextPayment = new Date(today.getFullYear(), today.getMonth(), paymentDay);
    if (nextPayment < today) {
      nextPayment.setMonth(nextPayment.getMonth() + 1);
    }
    return nextPayment.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className={`rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col ${
        isDark ? 'bg-gray-900' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex justify-between items-center ${
          isDark ? 'from-blue-700 to-purple-800' : ''
        }`}>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Search className="w-7 h-7" />
            Gestión de Usuarios
          </h2>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-white/20 rounded-lg transition-all transform hover:scale-110"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-y-auto p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
          {!selectedUser ? (
            <>
              {/* Search Form */}
              <form onSubmit={handleSearch} className="mb-6">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar por email o nombre..."
                    className={`flex-1 px-4 py-3 rounded-xl border-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 flex items-center gap-2 font-semibold shadow-lg transform hover:scale-105"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Search className="w-5 h-5" />
                        Buscar
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="space-y-3">
                  <h3 className={`text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Resultados ({searchResults.length})
                  </h3>
                  {searchResults.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => loadUserDetails(user.id)}
                      className={`w-full p-4 rounded-xl border-2 transition-all text-left transform hover:scale-[1.02] ${
                        isDark
                          ? 'bg-gray-800 border-gray-700 hover:border-blue-500 hover:bg-gray-700'
                          : 'bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {user.full_name}
                          </p>
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {/* User Details View */}
              <div className="space-y-6">
                {/* Back Button */}
                <button
                  onClick={() => setSelectedUser(null)}
                  className={`flex items-center gap-2 font-medium transition-colors ${
                    isDark 
                      ? 'text-blue-400 hover:text-blue-300' 
                      : 'text-blue-600 hover:text-blue-700'
                  }`}
                >
                  ← Volver a búsqueda
                </button>

                {/* 1. SECCIÓN DATOS DEL USUARIO */}
                <div className={`rounded-xl p-6 ${
                  isDark 
                    ? 'bg-gradient-to-r from-blue-900/50 to-purple-900/50 border border-blue-800' 
                    : 'bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200'
                }`}>
                  <h3 className={`text-2xl font-bold mb-4 flex items-center gap-3 ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    <User className="w-7 h-7" />
                    {selectedUser.user.full_name}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className={`flex items-center gap-3 p-3 rounded-lg ${
                      isDark ? 'bg-gray-800/50' : 'bg-white/50'
                    }`}>
                      <Mail className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      <div className="flex-1">
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email / Usuario</p>
                        <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          {selectedUser.user.email}
                        </p>
                      </div>
                    </div>
                    <div className={`flex items-center gap-3 p-3 rounded-lg ${
                      isDark ? 'bg-gray-800/50' : 'bg-white/50'
                    }`}>
                      <Lock className={`w-5 h-5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                      <div className="flex-1">
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Contraseña</p>
                        <div className="flex items-center gap-2">
                          <p className={`font-mono text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                            {visiblePasswords['user-pass'] ? (selectedUser.user.password_plain || '••••••••') : '••••••••'}
                          </p>
                          <button
                            onClick={() => togglePasswordVisibility('user-pass')}
                            className={`p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                          >
                            {visiblePasswords['user-pass'] ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className={`flex items-center gap-3 p-3 rounded-lg ${
                      isDark ? 'bg-gray-800/50' : 'bg-white/50'
                    }`}>
                      <Phone className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                      <div className="flex-1">
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Teléfono</p>
                        <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          {selectedUser.user.phone || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className={`flex items-center gap-3 p-3 rounded-lg ${
                      isDark ? 'bg-gray-800/50' : 'bg-white/50'
                    }`}>
                      <Globe className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                      <div className="flex-1">
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>País</p>
                        <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          {selectedUser.user.country_code}
                        </p>
                      </div>
                    </div>
                    <div className={`flex items-center gap-3 p-3 rounded-lg ${
                      isDark ? 'bg-gray-800/50' : 'bg-white/50'
                    }`}>
                      <Calendar className={`w-5 h-5 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                      <div className="flex-1">
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Fecha de Registro</p>
                        <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          {new Date(selectedUser.user.created_at).toLocaleDateString('es-MX')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. SECCIÓN LICENCIAMIENTO */}
                {selectedUser.licenses && selectedUser.licenses.length > 0 && (
                  <div className={`rounded-xl p-6 ${
                    isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                  }`}>
                    <h4 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <DollarSign className="w-6 h-6 text-green-500" />
                      Licenciamiento
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Estado:
                        </span>
                        <p className={`font-semibold text-lg ${
                          selectedUser.licenses[0].status === 'active' 
                            ? isDark ? 'text-green-400' : 'text-green-600'
                            : isDark ? 'text-orange-400' : 'text-orange-600'
                        }`}>
                          {selectedUser.licenses[0].status === 'active' ? 'Activo' : 'Prueba'}
                        </p>
                      </div>
                      <div>
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Plan Seleccionado:
                        </span>
                        <p className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {selectedUser.plan_name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Antigüedad:
                        </span>
                        <p className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {(() => {
                            const regDate = new Date(selectedUser.user.created_at);
                            const today = new Date();
                            const months = Math.floor((today - regDate) / (1000 * 60 * 60 * 24 * 30));
                            return `${months} ${months === 1 ? 'mes' : 'meses'}`;
                          })()}
                        </p>
                      </div>
                      <div>
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Próximo Pago:
                        </span>
                        <p className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {calculateNextPaymentDate(selectedUser.user.created_at)}
                        </p>
                      </div>
                      <div>
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Monto Mensual:
                        </span>
                        <p className={`font-semibold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          ${selectedUser.pricing?.total_monthly || 0} MXN
                        </p>
                      </div>
                    </div>
                    
                    {selectedUser.active_discount && (
                      <div className={`mt-4 p-3 rounded-lg ${
                        isDark ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
                      }`}>
                        <p className={`text-sm font-semibold flex items-center gap-2 ${
                          isDark ? 'text-green-400' : 'text-green-800'
                        }`}>
                          <Tag className="w-4 h-4" />
                          Descuento Activo: -${selectedUser.active_discount.amount} MXN
                        </p>
                        <p className={`text-xs mt-1 ${isDark ? 'text-green-500' : 'text-green-700'}`}>
                          {selectedUser.active_discount.type === 'permanent' ? 'Permanente' :
                           selectedUser.active_discount.type === 'once' ? 'Una vez' :
                           `Por ${selectedUser.active_discount.duration_months} meses`}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* 3. BOTONES DE ACCIÓN */}
                <div className={`rounded-xl p-6 ${
                  isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                }`}>
                  <h4 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Acciones Administrativas
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      onClick={handleAddRouter}
                      className={`group relative px-6 py-4 rounded-xl transition-all duration-300 overflow-hidden ${
                        isDark 
                          ? 'bg-gradient-to-br from-green-600 to-emerald-700 hover:from-green-500 hover:to-emerald-600'
                          : 'bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500'
                      } shadow-lg hover:shadow-xl transform hover:scale-105`}
                    >
                      <div className="relative z-10 flex items-center justify-center gap-3 text-white">
                        <div className="p-2 bg-white/20 rounded-lg">
                          <Plus className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-sm">Agregar</p>
                          <p className="text-xs opacity-90">Router</p>
                        </div>
                      </div>
                    </button>
                    
                    <button
                      onClick={handleAddSeller}
                      className={`group relative px-6 py-4 rounded-xl transition-all duration-300 overflow-hidden ${
                        isDark 
                          ? 'bg-gradient-to-br from-purple-600 to-pink-700 hover:from-purple-500 hover:to-pink-600'
                          : 'bg-gradient-to-br from-purple-500 to-pink-600 hover:from-purple-400 hover:to-pink-500'
                      } shadow-lg hover:shadow-xl transform hover:scale-105`}
                    >
                      <div className="relative z-10 flex items-center justify-center gap-3 text-white">
                        <div className="p-2 bg-white/20 rounded-lg">
                          <Plus className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-sm">Agregar</p>
                          <p className="text-xs opacity-90">Vendedor</p>
                        </div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => setShowDiscountModal(true)}
                      className={`group relative px-6 py-4 rounded-xl transition-all duration-300 overflow-hidden ${
                        isDark 
                          ? 'bg-gradient-to-br from-orange-600 to-red-700 hover:from-orange-500 hover:to-red-600'
                          : 'bg-gradient-to-br from-orange-500 to-red-600 hover:from-orange-400 hover:to-red-500'
                      } shadow-lg hover:shadow-xl transform hover:scale-105`}
                    >
                      <div className="relative z-10 flex items-center justify-center gap-3 text-white">
                        <div className="p-2 bg-white/20 rounded-lg">
                          <Tag className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-sm">Aplicar</p>
                          <p className="text-xs opacity-90">Descuento</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* 4. SECCIÓN TABS */}
                <div className={`rounded-xl p-6 ${
                  isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                }`}>
                  
                  {/* Tabs */}
                  <div className="flex gap-2 mb-6 flex-wrap">
                    <button
                      onClick={() => setActiveTab('routers')}
                      className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                        activeTab === 'routers'
                          ? isDark 
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-500 text-white'
                          : isDark
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      <Server className="w-5 h-5 inline mr-2" />
                      Routers ({selectedUser.routers?.length || 0})
                    </button>
                    <button
                      onClick={() => setActiveTab('planes')}
                      className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                        activeTab === 'planes'
                          ? isDark 
                            ? 'bg-green-600 text-white'
                            : 'bg-green-500 text-white'
                          : isDark
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Planes ({selectedUser.plans_count || 0})
                    </button>
                    <button
                      onClick={() => setActiveTab('pos')}
                      className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                        activeTab === 'pos'
                          ? isDark 
                            ? 'bg-purple-600 text-white'
                            : 'bg-purple-500 text-white'
                          : isDark
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      <Users className="w-5 h-5 inline mr-2" />
                      Puntos de Venta ({selectedUser.pos_count || 0})
                    </button>
                    <button
                      onClick={() => setActiveTab('vendedores')}
                      className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                        activeTab === 'vendedores'
                          ? isDark 
                            ? 'bg-orange-600 text-white'
                            : 'bg-orange-500 text-white'
                          : isDark
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      <Users className="w-5 h-5 inline mr-2" />
                      Vendedores ({selectedUser.sellers_count || 0})
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="space-y-4 min-h-[300px]">
                    
                    {/* TAB ROUTERS */}
                    {activeTab === 'routers' && (
                      <div className="space-y-4">
                        {selectedUser.routers && selectedUser.routers.length > 0 ? (
                          selectedUser.routers.map((router, index) => {
                            const colors = getRouterColor(index);
                            return (
                              <div key={router.id} className={`rounded-lg p-4 border-2 ${colors.bg} ${colors.border}`}>
                                <h4 className={`text-lg font-bold mb-3 ${colors.text}`}>
                                  <Server className="w-5 h-5 inline mr-2" />
                                  {router.name}
                                </h4>
                                
                                {/* Info básica */}
                                <div className={`grid grid-cols-2 gap-3 mb-4 p-3 rounded ${
                                  isDark ? 'bg-gray-900/50' : 'bg-white/50'
                                }`}>
                                  <div>
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Plataforma</p>
                                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{router.platform}</p>
                                  </div>
                                  <div>
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Puerto</p>
                                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{router.port}</p>
                                  </div>
                                  <div>
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Versión Firmware</p>
                                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{router.version || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Recursos</p>
                                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                      {router.plans_count} planes, {router.pos_count} POS, {router.sellers_count} vendedores
                                    </p>
                                  </div>
                                </div>

                                {/* Credenciales VPN */}
                                <div className={`rounded-lg p-4 ${
                                  isDark ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
                                }`}>
                                  <h5 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${
                                    isDark ? 'text-blue-400' : 'text-blue-700'
                                  }`}>
                                    <Wifi className="w-4 h-4" />
                                    Credenciales VPN
                                  </h5>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className={`text-xs font-medium block mb-1 ${
                                        isDark ? 'text-gray-400' : 'text-gray-600'
                                      }`}>
                                        <MapPin className="w-3 h-3 inline mr-1" />
                                        IP Interna
                                      </label>
                                      <div className={`px-3 py-2 rounded font-mono text-sm ${
                                        isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
                                      }`}>
                                        {router.vpn_internal_ip}
                                      </div>
                                    </div>
                                    <div>
                                      <label className={`text-xs font-medium block mb-1 ${
                                        isDark ? 'text-gray-400' : 'text-gray-600'
                                      }`}>
                                        Puerto
                                      </label>
                                      <div className={`px-3 py-2 rounded font-mono text-sm ${
                                        isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
                                      }`}>
                                        {router.vpn_external_port}
                                      </div>
                                    </div>
                                    <div>
                                      <label className={`text-xs font-medium block mb-1 ${
                                        isDark ? 'text-gray-400' : 'text-gray-600'
                                      }`}>
                                        Usuario VPN
                                      </label>
                                      <div className={`px-3 py-2 rounded font-mono text-sm ${
                                        isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
                                      }`}>
                                        {router.vpn_username}
                                      </div>
                                    </div>
                                    <div>
                                      <label className={`text-xs font-medium block mb-1 ${
                                        isDark ? 'text-gray-400' : 'text-gray-600'
                                      }`}>
                                        Contraseña VPN
                                      </label>
                                      <div className="flex items-center gap-2">
                                        <div className={`flex-1 px-3 py-2 rounded font-mono text-sm ${
                                          isDark ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-900'
                                        }`}>
                                          {visiblePasswords[`vpn-${router.id}`] ? router.vpn_password : '••••••••'}
                                        </div>
                                        <button
                                          onClick={() => togglePasswordVisibility(`vpn-${router.id}`)}
                                          className={`p-2 rounded ${
                                            isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                                          }`}
                                        >
                                          {visiblePasswords[`vpn-${router.id}`] ? (
                                            <EyeOff className="w-4 h-4" />
                                          ) : (
                                            <Eye className="w-4 h-4" />
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            No hay routers configurados
                          </p>
                        )}
                      </div>
                    )}

                    {/* TAB PLANES */}
                    {activeTab === 'planes' && (
                      <div className="space-y-4">
                        {selectedUser.routers && selectedUser.routers.length > 0 ? (
                          selectedUser.routers.map((router, routerIndex) => {
                            const colors = getRouterColor(routerIndex);
                            if (!router.plans || router.plans.length === 0) return null;
                            
                            return (
                              <div key={router.id} className={`rounded-lg p-4 border-2 ${colors.bg} ${colors.border}`}>
                                <h4 className={`text-md font-bold mb-3 ${colors.text}`}>
                                  {router.name}
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                  {router.plans.map((plan) => (
                                    <div key={plan.id} className={`p-3 rounded-lg ${
                                      isDark ? 'bg-gray-900/50' : 'bg-white/80'
                                    }`}>
                                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {plan.name}
                                      </p>
                                      <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {plan.plan_type}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            No hay planes configurados
                          </p>
                        )}
                      </div>
                    )}

                    {/* TAB PUNTOS DE VENTA */}
                    {activeTab === 'pos' && (
                      <div className="space-y-4">
                        {selectedUser.routers && selectedUser.routers.length > 0 ? (
                          selectedUser.routers.map((router, routerIndex) => {
                            const colors = getRouterColor(routerIndex);
                            if (!router.pos_detail || router.pos_detail.length === 0) return null;
                            
                            return (
                              <div key={router.id} className={`rounded-lg p-4 border-2 ${colors.bg} ${colors.border}`}>
                                <h4 className={`text-md font-bold mb-3 ${colors.text}`}>
                                  {router.name}
                                </h4>
                                <div className="space-y-3">
                                  {router.pos_detail.map((pos) => (
                                    <div key={pos.id} className={`p-3 rounded-lg ${
                                      isDark ? 'bg-gray-900/50' : 'bg-white/80'
                                    }`}>
                                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {pos.name}
                                      </p>
                                      <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {pos.plans_count} planes asignados | {pos.sellers_count} vendedores
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            No hay puntos de venta configurados
                          </p>
                        )}
                      </div>
                    )}

                    {/* TAB VENDEDORES */}
                    {activeTab === 'vendedores' && (
                      <div className="space-y-4">
                        {selectedUser.routers && selectedUser.routers.length > 0 ? (
                          selectedUser.routers.map((router, routerIndex) => {
                            const colors = getRouterColor(routerIndex);
                            
                            // Recolectar TODOS los vendedores del router (directos + los de POS)
                            const allSellersInRouter = [];
                            
                            // Vendedores directos del router
                            if (router.sellers && router.sellers.length > 0) {
                              allSellersInRouter.push(...router.sellers);
                            }
                            
                            // Vendedores dentro de los POS
                            if (router.pos_detail && router.pos_detail.length > 0) {
                              router.pos_detail.forEach(pos => {
                                if (pos.sellers && pos.sellers.length > 0) {
                                  allSellersInRouter.push(...pos.sellers);
                                }
                              });
                            }
                            
                            // Eliminar duplicados por ID
                            const uniqueSellers = allSellersInRouter.filter((seller, index, self) =>
                              index === self.findIndex((s) => s.id === seller.id)
                            );
                            
                            if (uniqueSellers.length === 0) return null;
                            
                            return (
                              <div key={router.id} className={`rounded-lg p-4 border-2 ${colors.bg} ${colors.border}`}>
                                <h4 className={`text-md font-bold mb-3 ${colors.text}`}>
                                  {router.name} ({uniqueSellers.length} vendedores)
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {uniqueSellers.map((seller) => (
                                    <div key={seller.id} className={`p-3 rounded-lg ${
                                      isDark ? 'bg-gray-900/50' : 'bg-white/80'
                                    }`}>
                                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {seller.name || seller.full_name}
                                      </p>
                                      <p className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {seller.phone || seller.email}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            No hay vendedores configurados
                          </p>
                        )}
                      </div>
                    )}

                  </div>
                </div>

              </div>
            </>
          )}
        </div>
      </div>

      {/* Discount Modal */}
      {showDiscountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className={`rounded-2xl shadow-2xl max-w-md w-full p-6 ${
            isDark ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Aplicar Descuento
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Monto del Descuento (MXN)
                </label>
                <input
                  type="number"
                  value={discountData.amount}
                  onChange={(e) => setDiscountData({...discountData, amount: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border-2 focus:ring-2 focus:ring-blue-500 ${
                    isDark 
                      ? 'bg-gray-900 border-gray-700 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="500"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Tipo de Descuento
                </label>
                <select
                  value={discountData.type}
                  onChange={(e) => setDiscountData({...discountData, type: e.target.value})}
                  className={`w-full px-4 py-2 rounded-lg border-2 focus:ring-2 focus:ring-blue-500 ${
                    isDark 
                      ? 'bg-gray-900 border-gray-700 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="once">Una vez (próximo pago)</option>
                  <option value="months">Por X meses</option>
                  <option value="permanent">Permanente</option>
                </select>
              </div>

              {discountData.type === 'months' && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Duración (meses)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={discountData.duration_months}
                    onChange={(e) => setDiscountData({...discountData, duration_months: parseInt(e.target.value)})}
                    className={`w-full px-4 py-2 rounded-lg border-2 focus:ring-2 focus:ring-blue-500 ${
                      isDark 
                        ? 'bg-gray-900 border-gray-700 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleApplyDiscount}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 font-semibold"
              >
                Aplicar
              </button>
              <button
                onClick={() => setShowDiscountModal(false)}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold ${
                  isDark 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSearchModal;
